import { useEffect, useMemo, useState } from "react";
import { useVendorPO, type PurchaseOrder, type POLineItem } from "@/data/VendorPOContext";
import { useProductRegistry, type RegistryProduct } from "@/data/ProductRegistryContext";
import { useOrderDraft } from "@/data/OrderDraftContext";
import { useAppData } from "@/data/AppDataContext";
import { useRole } from "@/data/RoleContext";
import {
  validateDraftForPush,
  describeBlockReason,
  type OrderDraftVendorGroup,
} from "@/data/orderDraft";
import { derivePreSpendGate } from "@/data/preSpendGate";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { Package, Truck, AlertTriangle, Send, Flag, ClipboardCheck, ShieldAlert } from "lucide-react";

export interface WorkOrderItem {
  sku: string;
  quantity: number;
}

export type POOrderingMode = "prep" | "push";

export interface OrderingModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  jobId: string;
  jobLabel: string;
  workOrderItems: WorkOrderItem[];
  /** "prep" saves an Order Draft (no vendor submission). "push" submits the prepared draft to vendors. */
  mode?: POOrderingMode;
  onOrderComplete?: (pos: PurchaseOrder[]) => void;
  onPrepComplete?: () => void;
}

export function POOrderingModal({
  open,
  onOpenChange,
  jobId,
  jobLabel,
  workOrderItems,
  mode = "prep",
  onOrderComplete,
  onPrepComplete,
}: OrderingModalProps) {
  const { vendors, setPurchaseOrders, generatePONumber } = useVendorPO();
  const { products: registryProducts } = useProductRegistry();
  const { jobs } = useAppData();
  const { role, roleInfo } = useRole();
  const { getDraft, prepareDraft, markPushed, logIntegrityViolation } = useOrderDraft();

  const job = useMemo(() => jobs.find((j) => j.id === jobId), [jobs, jobId]);
  const draft = getDraft(jobId);

  const [notes, setNotes] = useState("");
  const [flagMode, setFlagMode] = useState(false);
  const [flagReason, setFlagReason] = useState("");
  const [selectedVendors, setSelectedVendors] = useState<Set<string>>(new Set());

  // Resolve work order items to products and group by vendor
  const vendorGroups = useMemo(() => {
    const groups: Record<string, { vendor: typeof vendors[0]; items: (POLineItem & { product: RegistryProduct })[] }> = {};

    for (const woItem of workOrderItems) {
      const product = registryProducts.find((p) => p.sku === woItem.sku);
      if (!product) continue;

      const vendorName = product.vendor;
      const vendor = vendors.find((v) => v.vendorName === vendorName && v.active);
      if (!vendor) continue;

      if (!groups[vendor.vendorId]) {
        groups[vendor.vendorId] = { vendor, items: [] };
      }
      groups[vendor.vendorId].items.push({
        sku: product.sku,
        productName: product.productName,
        quantity: woItem.quantity,
        unitCost: product.cost,
        lineTotal: product.cost * woItem.quantity,
        product,
      });
    }
    return groups;
  }, [workOrderItems, registryProducts, vendors]);

  const unresolvedSkus = useMemo(() => {
    return workOrderItems.filter((wo) => {
      const product = registryProducts.find((p) => p.sku === wo.sku);
      if (!product) return true;
      const vendor = vendors.find((v) => v.vendorName === product.vendor && v.active);
      return !vendor;
    });
  }, [workOrderItems, registryProducts, vendors]);

  const allVendorIds = Object.keys(vendorGroups);
  const allSelected = allVendorIds.length > 0 && allVendorIds.every((id) => selectedVendors.has(id));

  const toggleVendor = (id: string) => {
    setSelectedVendors((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const selectAll = () => {
    if (allSelected) setSelectedVendors(new Set());
    else setSelectedVendors(new Set(allVendorIds));
  };

  // Initialize selection on open. In Push mode, lock to draft's vendor list.
  useEffect(() => {
    if (!open) return;
    if (mode === "push" && draft?.status === "prepared") {
      const draftVendorIds = new Set(draft.vendorGroups.map((g) => g.vendorId));
      setSelectedVendors(draftVendorIds);
      setNotes(draft.notes || "");
    } else {
      setSelectedVendors(new Set(allVendorIds));
      setNotes("");
    }
  }, [open, mode, draft?.draftId]);

  const totalCost = useMemo(() => {
    let total = 0;
    for (const id of selectedVendors) {
      const group = vendorGroups[id];
      if (group) total += group.items.reduce((s, i) => s + i.lineTotal, 0);
    }
    return total;
  }, [selectedVendors, vendorGroups]);

  // Push validation (only relevant in push mode)
  const pushValidation = useMemo(() => {
    if (mode !== "push" || !job) return null;
    return validateDraftForPush(draft, job);
  }, [mode, job, draft]);

  // Pre-spend gate (display in prep mode for context)
  const gate = useMemo(() => job ? derivePreSpendGate(job) : null, [job]);

  const isReadOnly = mode === "push";
  const draftVendorIds = useMemo(
    () => new Set((draft?.vendorGroups || []).map((g) => g.vendorId)),
    [draft],
  );

  // ── Actions ────────────────────────────────────────────────────────────

  const handlePrep = () => {
    if (selectedVendors.size === 0) { toast.error("Select at least one vendor group"); return; }
    if (!job) { toast.error("Job not found"); return; }
    if (gate && !gate.cleared) {
      toast.error("Cannot prep order", { description: gate.shortReason });
      return;
    }

    const draftGroups: OrderDraftVendorGroup[] = [];
    for (const vendorId of selectedVendors) {
      const group = vendorGroups[vendorId];
      if (!group) continue;
      draftGroups.push({
        vendorId: group.vendor.vendorId,
        vendorName: group.vendor.vendorName,
        totalCost: group.items.reduce((s, i) => s + i.lineTotal, 0),
        items: group.items.map((i) => ({
          sku: i.sku,
          productName: i.productName,
          quantity: i.quantity,
          vendorName: group.vendor.vendorName,
        })),
      });
    }

    prepareDraft({
      jobId,
      vendorGroups: draftGroups,
      preparedBy: roleInfo?.label || role,
      notes,
    });

    toast.success("Order prepared", {
      description: `${draftGroups.length} vendor group(s) ready for review. Push when ready.`,
    });
    onPrepComplete?.();
    onOpenChange(false);
    setNotes(""); setFlagMode(false); setFlagReason("");
  };

  const handlePush = () => {
    if (!job || !draft || mode !== "push") return;
    const v = validateDraftForPush(draft, job);
    if (!v.ok && v.reason) {
      const r = v.reason;
      if (r.kind === "integrity_violation") {
        logIntegrityViolation({
          jobId,
          expectedHash: r.expectedHash,
          actualHash: r.actualHash,
          description: `Push attempted on ${jobLabel}. BOM hash drift detected with no Signed Change Order on file.`,
        });
        toast.error("Push blocked — integrity violation", {
          description: "Logged. Re-prep required before pushing.",
        });
        onOpenChange(false);
        return;
      }
      toast.error("Push blocked", { description: describeBlockReason(r) });
      return;
    }

    // Submit POs from the draft snapshot (not from current vendorGroups).
    const newPOs: PurchaseOrder[] = [];
    for (const group of draft.vendorGroups) {
      // Resolve unit cost from registry at push time (cost is allowed to change;
      // BOM is what's locked). Roles without cost visibility never see this.
      const items: POLineItem[] = group.items.map((it) => {
        const product = registryProducts.find((p) => p.sku === it.sku);
        const unitCost = product?.cost ?? 0;
        return {
          sku: it.sku,
          productName: it.productName,
          quantity: it.quantity,
          unitCost,
          lineTotal: unitCost * it.quantity,
        };
      });
      const po: PurchaseOrder = {
        poId: "po-" + Math.random().toString(36).slice(2, 10),
        poNumber: generatePONumber(),
        jobId,
        jobLabel,
        vendorId: group.vendorId,
        vendorName: group.vendorName,
        items,
        status: "Submitted",
        totalCost: items.reduce((s, i) => s + i.lineTotal, 0),
        createdAt: new Date().toISOString(),
        submittedAt: new Date().toISOString(),
        notes: draft.notes || "",
        flagged: false,
      };
      newPOs.push(po);
    }

    setPurchaseOrders((prev) => [...prev, ...newPOs]);
    markPushed(jobId, newPOs.map((p) => p.poId), roleInfo?.label || role);
    toast.success(`${newPOs.length} PO(s) pushed to vendor${newPOs.length > 1 ? "s" : ""}`);
    onOrderComplete?.(newPOs);
    onOpenChange(false);
  };

  const handleFlag = () => {
    if (!flagReason.trim()) { toast.error("Please provide a reason"); return; }
    toast.warning(`Order flagged: ${flagReason}`);
    onOpenChange(false);
    setFlagMode(false); setFlagReason("");
  };

  // Disabled state for the primary CTA
  const primaryDisabled = mode === "prep"
    ? selectedVendors.size === 0 || unresolvedSkus.length > 0 || (gate ? !gate.cleared : false)
    : !pushValidation?.ok;

  const headerIcon = mode === "prep" ? ClipboardCheck : Send;
  const HeaderIcon = headerIcon;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <HeaderIcon className="h-5 w-5" />
            {mode === "prep" ? "Prep Order" : "Push to Vendor"} — {jobLabel}
            <Badge variant="outline" className="ml-1 text-[10px] uppercase tracking-wide">
              {mode === "prep" ? "Draft · No spend" : "Submits to vendor"}
            </Badge>
          </DialogTitle>
        </DialogHeader>

        {/* Pre-spend gate (prep mode) */}
        {mode === "prep" && gate && !gate.cleared && (
          <div className="bg-warning/10 border border-warning/30 rounded-md p-3">
            <p className="text-xs font-medium text-warning flex items-center gap-1">
              <AlertTriangle className="h-3 w-3" /> {gate.shortReason}
            </p>
            <p className="text-[10px] text-muted-foreground mt-1">
              All pre-spend gates must clear before an order can be prepared.
            </p>
          </div>
        )}

        {/* Push validation surface */}
        {mode === "push" && pushValidation && !pushValidation.ok && pushValidation.reason && (
          <div className={`rounded-md p-3 border ${
            pushValidation.reason.kind === "integrity_violation"
              ? "bg-destructive/10 border-destructive/30"
              : "bg-warning/10 border-warning/30"
          }`}>
            <p className={`text-xs font-medium flex items-center gap-1 ${
              pushValidation.reason.kind === "integrity_violation" ? "text-destructive" : "text-warning"
            }`}>
              {pushValidation.reason.kind === "integrity_violation"
                ? <ShieldAlert className="h-3 w-3" />
                : <AlertTriangle className="h-3 w-3" />}
              {describeBlockReason(pushValidation.reason)}
            </p>
            {pushValidation.reason.kind === "integrity_violation" && (
              <p className="text-[10px] text-destructive/80 mt-1 font-mono">
                expected: {pushValidation.reason.expectedHash} · actual: {pushValidation.reason.actualHash}
              </p>
            )}
          </div>
        )}

        {/* Push mode summary card */}
        {mode === "push" && draft && (
          <div className="bg-muted/40 border rounded-md p-3 text-[11px] space-y-1">
            <p><span className="text-muted-foreground">Prepared by:</span> <span className="font-medium">{draft.preparedBy}</span></p>
            <p><span className="text-muted-foreground">Prepared at:</span> <span className="font-medium">{new Date(draft.preparedAt).toLocaleString()}</span></p>
            <p><span className="text-muted-foreground">Source spec:</span> <span className="font-mono">{draft.sourceSpecRef}</span></p>
            <p><span className="text-muted-foreground">BOM hash:</span> <span className="font-mono">{draft.sourceBomHash}</span></p>
          </div>
        )}

        {/* Unresolved SKUs Warning (prep mode only — push uses snapshot) */}
        {mode === "prep" && unresolvedSkus.length > 0 && (
          <div className="bg-destructive/10 border border-destructive/30 rounded-md p-3">
            <p className="text-xs font-medium text-destructive flex items-center gap-1">
              <AlertTriangle className="h-3 w-3" /> {unresolvedSkus.length} SKU(s) cannot be ordered — missing vendor
            </p>
            <ul className="text-[10px] text-destructive mt-1 space-y-0.5">
              {unresolvedSkus.map((u) => <li key={u.sku} className="font-mono">{u.sku} × {u.quantity}</li>)}
            </ul>
          </div>
        )}

        {/* Vendor Groups */}
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <p className="text-xs font-medium">{Object.keys(vendorGroups).length} Vendor Group(s)</p>
            {!isReadOnly && (
              <div className="flex items-center gap-2">
                <Checkbox checked={allSelected} onCheckedChange={selectAll} />
                <span className="text-[10px] text-muted-foreground">Select All</span>
              </div>
            )}
          </div>

          {Object.entries(vendorGroups).map(([vendorId, group]) => {
            const selected = isReadOnly ? draftVendorIds.has(vendorId) : selectedVendors.has(vendorId);
            const groupTotal = group.items.reduce((s, i) => s + i.lineTotal, 0);
            // In push mode, dim groups that aren't in the draft.
            const dimmed = isReadOnly && !draftVendorIds.has(vendorId);
            return (
              <div
                key={vendorId}
                className={`border rounded-lg p-3 transition-colors ${
                  selected ? "border-primary/50 bg-primary/5" : "border-border"
                } ${dimmed ? "opacity-50" : ""}`}
              >
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    {!isReadOnly && (
                      <Checkbox checked={selected} onCheckedChange={() => toggleVendor(vendorId)} />
                    )}
                    <div>
                      <p className="text-xs font-bold flex items-center gap-1">
                        <Truck className="h-3 w-3" /> {group.vendor.vendorName}
                        {dimmed && <Badge variant="outline" className="text-[9px]">not in draft</Badge>}
                      </p>
                      <p className="text-[10px] text-muted-foreground">{group.vendor.contactName} · {group.vendor.orderingMethod}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-xs font-bold font-mono">${groupTotal.toLocaleString()}</p>
                    <p className="text-[10px] text-muted-foreground">{group.items.length} item(s)</p>
                  </div>
                </div>
                <div className="border-t pt-2 space-y-1">
                  {group.items.map((item) => (
                    <div key={item.sku} className="flex items-center justify-between text-[10px]">
                      <div className="flex items-center gap-2">
                        <span className="font-mono text-muted-foreground">{item.sku}</span>
                        <span>{item.productName}</span>
                      </div>
                      <div className="flex items-center gap-3">
                        <span>×{item.quantity}</span>
                        <span className="font-mono w-16 text-right">${item.lineTotal.toLocaleString()}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>

        {/* Summary */}
        <div className="border-t pt-3">
          <div className="flex items-center justify-between mb-3">
            <p className="text-xs font-medium">
              Order Total ({isReadOnly ? draft?.vendorGroups.length || 0 : selectedVendors.size} PO{(isReadOnly ? (draft?.vendorGroups.length || 0) : selectedVendors.size) !== 1 ? "s" : ""})
            </p>
            <p className="text-sm font-bold font-mono">
              ${(isReadOnly ? (draft?.vendorGroups.reduce((s, g) => s + g.totalCost, 0) || 0) : totalCost).toLocaleString()}
            </p>
          </div>
          {!isReadOnly && (
            <div>
              <Label className="text-xs">Notes (optional)</Label>
              <Textarea className="text-xs mt-1" rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Special instructions for vendors..." />
            </div>
          )}
          {isReadOnly && draft?.notes && (
            <div className="text-[11px] bg-muted/40 rounded p-2">
              <p className="text-muted-foreground mb-0.5">Notes from prep:</p>
              <p>{draft.notes}</p>
            </div>
          )}
        </div>

        {/* Flag Mode */}
        {flagMode && (
          <div className="border border-destructive/30 rounded-md p-3 bg-destructive/5">
            <Label className="text-xs text-destructive">Flag Reason *</Label>
            <Textarea className="text-xs mt-1" rows={2} value={flagReason} onChange={(e) => setFlagReason(e.target.value)} placeholder="Describe the issue..." />
          </div>
        )}

        <DialogFooter className="flex-col sm:flex-row gap-2">
          {!flagMode ? (
            <>
              <Button variant="outline" className="text-destructive" onClick={() => setFlagMode(true)}>
                <Flag className="h-3 w-3 mr-1" /> Flag Issue
              </Button>
              <div className="flex-1" />
              <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
              {mode === "prep" ? (
                <Button onClick={handlePrep} disabled={primaryDisabled}>
                  <ClipboardCheck className="h-3 w-3 mr-1" /> Save as Draft
                </Button>
              ) : (
                <Button onClick={handlePush} disabled={primaryDisabled}>
                  <Send className="h-3 w-3 mr-1" /> Push to Vendor{(draft?.vendorGroups.length || 0) > 1 ? "s" : ""}
                </Button>
              )}
            </>
          ) : (
            <>
              <Button variant="outline" onClick={() => setFlagMode(false)}>Cancel Flag</Button>
              <div className="flex-1" />
              <Button variant="destructive" onClick={handleFlag}>
                <AlertTriangle className="h-3 w-3 mr-1" /> Submit Flag
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
